import type { CandlestickData, IChartApi, LineData, Time } from 'lightweight-charts';

export type ChartRangeMeasureDirection = 'long1_short2' | 'short1_long2';

type FundingSettlementPoint = { time: Time; value: number };

export interface ChartRangeMeasureFundingConfig {
  getDirection: () => ChartRangeMeasureDirection;
  getExchange1Label: () => string;
  getExchange2Label: () => string;
  getExchange1Settlements: () => FundingSettlementPoint[];
  getExchange2Settlements: () => FundingSettlementPoint[];
  getExchange1Candles?: () => CandlestickData<Time>[];
  getExchange2Candles?: () => CandlestickData<Time>[];
}

export type ChartRangeMeasureSeries =
  | {
      kind: 'candle';
      label: string;
      color: string;
      getData: () => CandlestickData<Time>[];
    }
  | {
      kind: 'line';
      label: string;
      color: string;
      getData: () => LineData<Time>[];
      formatValue?: (value: number) => string;
    };

export interface ChartRangeMeasureSpreadConfig {
  getOpenSpread: () => LineData<Time>[];
  getCloseSpread: () => LineData<Time>[];
  formatValue?: (value: number) => string;
  /** Chart stores spread as rawRatio * scale. Default 100 (chart 1.0 = 1%). Use 1 for raw ratio charts. */
  chartSpreadScale?: number;
  openLabel?: string;
  closeLabel?: string;
  primaryLabel?: string;
}

export interface ChartRangeMeasurePane {
  paneIndex: number;
  series: ChartRangeMeasureSeries[];
  spread?: ChartRangeMeasureSpreadConfig;
  funding?: ChartRangeMeasureFundingConfig;
  /** spread: 价差区域原有框选；kline: 仅 X 轴时间区间 + close 涨跌 + 独立资费 */
  measureMode?: 'spread' | 'kline';
}

export interface AttachChartRangeMeasureOptions {
  panes: ChartRangeMeasurePane[];
}

interface RangeStats {
  bars: number;
  durationSec: number;
  changePct: number;
  startPrice: number;
  endPrice: number;
  high: number;
  low: number;
  amplitudePct: number;
}

function timeToUnix(time: Time): number {
  if (typeof time === 'number') return time;
  if (typeof time === 'string') return Math.floor(new Date(time).getTime() / 1000);
  return Math.floor(Date.UTC(time.year, time.month - 1, time.day) / 1000);
}

function formatDuration(seconds: number): string {
  const safe = Math.max(0, seconds);
  if (safe < 60) return `${safe}s`;
  if (safe < 3600) return `${Math.round(safe / 60)}m`;
  if (safe < 86400) return `${(safe / 3600).toFixed(1)}h`;
  return `${(safe / 86400).toFixed(1)}d`;
}

function formatPct(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

const DEFAULT_CHART_SPREAD_SCALE = 100;

function chartValueToRawSpreadRate(value: number, scale = DEFAULT_CHART_SPREAD_SCALE): number {
  return value / scale;
}

/** Equal notional per leg: return on total capital = signed raw rate sum / 2. */
function rawImpactToReturnRate(rawImpact: number): number {
  return rawImpact / 2;
}

/** Short spread return on total capital from open/close chart values. */
function computeSpreadReturnRate(
  startOpenChart: number,
  endCloseChart: number,
  scale = DEFAULT_CHART_SPREAD_SCALE,
): number {
  return (
    (chartValueToRawSpreadRate(startOpenChart, scale) -
      chartValueToRawSpreadRate(endCloseChart, scale)) /
    2
  );
}

function formatReturnOnCapitalRate(rate: number): string {
  if (!Number.isFinite(rate)) return '—';
  const sign = rate >= 0 ? '+' : '';
  return `${sign}${(rate * 100).toFixed(4)}%`;
}

const SECONDS_PER_YEAR = 365 * 24 * 60 * 60;

/** Simple annualized return (APR): linear extrapolation, avoids compound blow-up on short ranges. */
function computeAnnualizedApr(returnRate: number, durationSec: number): number | null {
  if (!Number.isFinite(returnRate) || !Number.isFinite(durationSec) || durationSec <= 0) {
    return null;
  }
  return returnRate * (SECONDS_PER_YEAR / durationSec);
}

function formatAprRate(rate: number): string {
  if (!Number.isFinite(rate)) return '—';
  const sign = rate >= 0 ? '+' : '';
  const pct = rate * 100;
  const decimals = Math.abs(pct) >= 100 ? 2 : 4;
  return `${sign}${pct.toFixed(decimals)}%`;
}

/** Spread rate (ratio) — not a percentage label; multiply by 100 only when showing %. */
function formatSpreadRawRate(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return value.toFixed(4);
}

function formatSignedSpreadRawRate(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(4)}`;
}

function formatPrice(value: number): string {
  if (!Number.isFinite(value)) return '-';
  if (Math.abs(value) >= 1000) return value.toFixed(2);
  if (Math.abs(value) >= 1) return value.toFixed(4);
  return value.toFixed(6);
}

function pointsInTimeRange<T extends { time: Time }>(data: T[], t0: Time, t1: Time): T[] {
  const lo = Math.min(timeToUnix(t0), timeToUnix(t1));
  const hi = Math.max(timeToUnix(t0), timeToUnix(t1));
  return data.filter((point) => {
    const t = timeToUnix(point.time);
    return t >= lo && t <= hi;
  });
}

function findLineValueAtTime(data: LineData<Time>[], target: Time): number | null {
  if (data.length === 0) return null;
  const targetUnix = timeToUnix(target);
  const sorted = [...data].sort((a, b) => timeToUnix(a.time) - timeToUnix(b.time));
  const exact = sorted.find((point) => timeToUnix(point.time) === targetUnix);
  if (exact) return exact.value;

  let nearest = sorted[0];
  let nearestDist = Math.abs(timeToUnix(nearest.time) - targetUnix);
  for (const point of sorted) {
    const dist = Math.abs(timeToUnix(point.time) - targetUnix);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = point;
    }
  }
  return nearest.value;
}

function findCandleAtTime(data: CandlestickData<Time>[], target: Time): CandlestickData<Time> | null {
  if (data.length === 0) return null;
  const targetUnix = timeToUnix(target);
  const sorted = [...data].sort((a, b) => timeToUnix(a.time) - timeToUnix(b.time));
  const exact = sorted.find((point) => timeToUnix(point.time) === targetUnix);
  if (exact) return exact;

  let nearest = sorted[0];
  let nearestDist = Math.abs(timeToUnix(nearest.time) - targetUnix);
  for (const point of sorted) {
    const dist = Math.abs(timeToUnix(point.time) - targetUnix);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = point;
    }
  }
  return nearest;
}

function computePriceChangePct(startPrice: number, endPrice: number): number {
  if (!Number.isFinite(startPrice) || startPrice === 0) return 0;
  return ((endPrice - startPrice) / startPrice) * 100;
}

/** Long gains when price rises; short gains when price falls. */
function legPriceImpactPct(changePct: number, role: 'long' | 'short'): number {
  return role === 'long' ? changePct : -changePct;
}

interface SpreadRangeMetrics {
  returnRate: number;
  startOpenRaw: number;
  endCloseRaw: number;
  netSpreadChangeRaw: number;
  durationSec: number;
  bars: number;
  openLabel: string;
  closeLabel: string;
  primaryLabel: string;
}

function computeSpreadRangeMetrics(
  spread: ChartRangeMeasureSpreadConfig,
  t0: Time,
  t1: Time,
): SpreadRangeMetrics | null {
  const startTime = timeToUnix(t0) <= timeToUnix(t1) ? t0 : t1;
  const endTime = timeToUnix(t0) <= timeToUnix(t1) ? t1 : t0;
  const openData = spread.getOpenSpread();
  const closeData = spread.getCloseSpread();
  const startOpen = findLineValueAtTime(openData, startTime);
  const endClose = findLineValueAtTime(closeData, endTime);
  if (startOpen == null || endClose == null) return null;

  const scale = spread.chartSpreadScale ?? DEFAULT_CHART_SPREAD_SCALE;
  const startOpenRaw = chartValueToRawSpreadRate(startOpen, scale);
  const endCloseRaw = chartValueToRawSpreadRate(endClose, scale);
  const openInRange = pointsInTimeRange(openData, startTime, endTime);
  const closeInRange = pointsInTimeRange(closeData, startTime, endTime);

  return {
    returnRate: computeSpreadReturnRate(startOpen, endClose, scale),
    startOpenRaw,
    endCloseRaw,
    netSpreadChangeRaw: startOpenRaw - endCloseRaw,
    durationSec: Math.max(0, timeToUnix(endTime) - timeToUnix(startTime)),
    bars: Math.max(openInRange.length, closeInRange.length),
    openLabel: spread.openLabel ?? 'Open Spread',
    closeLabel: spread.closeLabel ?? 'Close Spread',
    primaryLabel: spread.primaryLabel ?? '价差收益',
  };
}

function renderSpreadRangeSummary(metrics: SpreadRangeMetrics): string {
  const profitColor = metrics.returnRate >= 0 ? '#16a34a' : '#dc2626';
  const netChangeColor = metrics.netSpreadChangeRaw >= 0 ? '#16a34a' : '#dc2626';

  return (
    `<div class="chart-range-measure-row">` +
      `<span class="chart-range-measure-series">${metrics.primaryLabel}</span>` +
      `<span class="chart-range-measure-change" style="color:${profitColor}">${formatReturnOnCapitalRate(metrics.returnRate)}</span>` +
    `</div>` +
    `<div class="chart-range-measure-meta">` +
      `${metrics.bars} 点 · ${formatDuration(metrics.durationSec)}` +
    `</div>` +
    `<div class="chart-range-measure-meta chart-range-measure-meta-sub">` +
      `${metrics.openLabel} ${formatSpreadRawRate(metrics.startOpenRaw)} → ` +
      `${metrics.closeLabel} ${formatSpreadRawRate(metrics.endCloseRaw)} · ` +
      `净变化 <span style="color:${netChangeColor}">${formatSignedSpreadRawRate(metrics.netSpreadChangeRaw)}</span>` +
    `</div>`
  );
}

function computeCandleRangeStats(candles: CandlestickData<Time>[]): RangeStats | null {
  if (candles.length === 0) return null;
  const sorted = [...candles].sort((a, b) => timeToUnix(a.time) - timeToUnix(b.time));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const startPrice = first.open;
  const endPrice = last.close;
  const changePct = startPrice !== 0 ? ((endPrice - startPrice) / startPrice) * 100 : 0;
  const high = Math.max(...sorted.map((c) => c.high));
  const low = Math.min(...sorted.map((c) => c.low));
  const amplitudePct = low !== 0 ? ((high - low) / low) * 100 : 0;
  const durationSec = Math.max(0, timeToUnix(last.time) - timeToUnix(first.time));
  return {
    bars: sorted.length,
    durationSec,
    changePct,
    startPrice,
    endPrice,
    high,
    low,
    amplitudePct,
  };
}

function computeLineRangeStats(points: LineData<Time>[]): RangeStats | null {
  if (points.length === 0) return null;
  const sorted = [...points].sort((a, b) => timeToUnix(a.time) - timeToUnix(b.time));
  const values = sorted.map((point) => point.value);
  const startPrice = values[0];
  const endPrice = values[values.length - 1];
  const changePct =
    startPrice !== 0 ? ((endPrice - startPrice) / Math.abs(startPrice)) * 100 : 0;
  const high = Math.max(...values);
  const low = Math.min(...values);
  const amplitudePct = low !== 0 ? ((high - low) / Math.abs(low)) * 100 : 0;
  const durationSec = Math.max(0, timeToUnix(sorted[sorted.length - 1].time) - timeToUnix(sorted[0].time));
  return {
    bars: sorted.length,
    durationSec,
    changePct,
    startPrice,
    endPrice,
    high,
    low,
    amplitudePct,
  };
}

function normalizeTimeRange(t0: Time, t1: Time): { startTime: Time; endTime: Time } {
  return timeToUnix(t0) <= timeToUnix(t1)
    ? { startTime: t0, endTime: t1 }
    : { startTime: t1, endTime: t0 };
}

/** K-line pane: time range from drag X; prices at start/end times from selected candles. */
function computeKlineCandleStats(
  candles: CandlestickData<Time>[],
  t0: Time,
  t1: Time,
): RangeStats | null {
  const { startTime, endTime } = normalizeTimeRange(t0, t1);
  const inRange = pointsInTimeRange(candles, startTime, endTime);
  if (inRange.length === 0) return null;

  const startCandle = findCandleAtTime(candles, startTime);
  const endCandle = findCandleAtTime(candles, endTime);
  if (!startCandle || !endCandle) return null;

  const startPrice = startCandle.close;
  const endPrice = endCandle.close;
  const changePct = computePriceChangePct(startPrice, endPrice);
  const high = Math.max(...inRange.map((candle) => candle.high));
  const low = Math.min(...inRange.map((candle) => candle.low));
  const amplitudePct = low !== 0 ? ((high - low) / low) * 100 : 0;

  return {
    bars: inRange.length,
    durationSec: Math.max(0, timeToUnix(endTime) - timeToUnix(startTime)),
    changePct,
    startPrice,
    endPrice,
    high,
    low,
    amplitudePct,
  };
}

/** Use x-axis boundary times only: value at start vs value at end (nearest point if needed). */
function computeLineBoundaryStats(points: LineData<Time>[], t0: Time, t1: Time): RangeStats | null {
  if (points.length === 0) return null;
  const { startTime, endTime } = normalizeTimeRange(t0, t1);
  const startPrice = findLineValueAtTime(points, startTime);
  const endPrice = findLineValueAtTime(points, endTime);
  if (startPrice == null || endPrice == null) return null;

  const changePct =
    startPrice !== 0 ? ((endPrice - startPrice) / Math.abs(startPrice)) * 100 : 0;
  const inRange = pointsInTimeRange(points, startTime, endTime);
  const rangeValues =
    inRange.length > 0 ? inRange.map((point) => point.value) : [startPrice, endPrice];
  const high = Math.max(...rangeValues);
  const low = Math.min(...rangeValues);
  const amplitudePct = low !== 0 ? ((high - low) / Math.abs(low)) * 100 : 0;

  return {
    bars: inRange.length > 0 ? inRange.length : 2,
    durationSec: Math.max(0, timeToUnix(endTime) - timeToUnix(startTime)),
    changePct,
    startPrice,
    endPrice,
    high,
    low,
    amplitudePct,
  };
}

function computeSeriesStats(
  item: ChartRangeMeasureSeries,
  t0: Time,
  t1: Time,
  measureMode: 'spread' | 'kline',
): RangeStats | null {
  if (measureMode === 'kline') {
    if (item.kind === 'candle') {
      return computeKlineCandleStats(item.getData(), t0, t1);
    }
    return computeLineBoundaryStats(item.getData(), t0, t1);
  }
  if (item.kind === 'candle') {
    return computeCandleRangeStats(pointsInTimeRange(item.getData(), t0, t1));
  }
  return computeLineRangeStats(pointsInTimeRange(item.getData(), t0, t1));
}

function formatSeriesValue(item: ChartRangeMeasureSeries, value: number): string {
  if (item.kind === 'line' && item.formatValue) {
    return item.formatValue(value);
  }
  return formatPrice(value);
}

function formatSeriesDelta(item: ChartRangeMeasureSeries, delta: number): string {
  const sign = delta >= 0 ? '+' : '-';
  const body =
    item.kind === 'line' && item.formatValue
      ? item.formatValue(Math.abs(delta))
      : formatPrice(Math.abs(delta));
  return `${sign}${body}`;
}

function fundingImpactColor(rate: number): string {
  if (rate >= 0) return '#16a34a';
  if (rate < 0) return '#dc2626';
  return '#64748b';
}

function exchangeLegRole(direction: ChartRangeMeasureDirection, exchange: 1 | 2): 'long' | 'short' {
  if (direction === 'long1_short2') return exchange === 1 ? 'long' : 'short';
  return exchange === 1 ? 'short' : 'long';
}

/** Chart funding values are rate * 100; convert to signed raw rate impact for the direction leg. */
function settlementLegImpact(
  direction: ChartRangeMeasureDirection,
  exchange: 1 | 2,
  chartRatePct: number,
): number {
  const rate = chartRatePct / 100;
  const role = exchangeLegRole(direction, exchange);
  return settlementRoleImpact(rate, role);
}

/** Long pays positive funding; short receives positive funding. */
function settlementRoleImpact(rate: number, role: 'long' | 'short'): number {
  return role === 'long' ? -rate : rate;
}

function settlementChartRateImpact(chartRatePct: number, role: 'long' | 'short'): number {
  return settlementRoleImpact(chartRatePct / 100, role);
}

/** Match RealtimeSpread funding settlement alignment (ms). */
function fundingSettlementAlignmentToleranceSec(intervalSeconds: number): number {
  return Math.max(5, Math.floor(intervalSeconds));
}

type SettlementLegEvent = {
  timeSec: number;
  exchange: 1 | 2;
  value: number;
};

/** Net signed funding rate at settlement times for the current direction (chart scale: rate * 100). */
export function buildNetFundingSettlementLine(
  direction: ChartRangeMeasureDirection,
  exchange1Settlements: FundingSettlementPoint[],
  exchange2Settlements: FundingSettlementPoint[],
  intervalSeconds: number,
): LineData<Time>[] {
  const toleranceSec = fundingSettlementAlignmentToleranceSec(intervalSeconds);
  const events: SettlementLegEvent[] = [];

  for (const point of exchange1Settlements) {
    const timeSec = Number(point.time);
    if (!Number.isFinite(timeSec) || !Number.isFinite(point.value)) continue;
    events.push({ timeSec, exchange: 1, value: point.value });
  }
  for (const point of exchange2Settlements) {
    const timeSec = Number(point.time);
    if (!Number.isFinite(timeSec) || !Number.isFinite(point.value)) continue;
    events.push({ timeSec, exchange: 2, value: point.value });
  }

  if (events.length === 0) return [];

  events.sort((left, right) => left.timeSec - right.timeSec);

  const rows: LineData<Time>[] = [];
  let clusterStart = 0;

  while (clusterStart < events.length) {
    const anchorTimeSec = events[clusterStart].timeSec;
    let clusterEnd = clusterStart + 1;
    while (
      clusterEnd < events.length &&
      events[clusterEnd].timeSec - anchorTimeSec <= toleranceSec
    ) {
      clusterEnd += 1;
    }

    let ex1Rate: number | undefined;
    let ex2Rate: number | undefined;
    for (let index = clusterStart; index < clusterEnd; index += 1) {
      const event = events[index];
      if (event.exchange === 1) ex1Rate = event.value;
      else ex2Rate = event.value;
    }

    let netImpact = 0;
    if (ex1Rate != null) netImpact += settlementLegImpact(direction, 1, ex1Rate);
    if (ex2Rate != null) netImpact += settlementLegImpact(direction, 2, ex2Rate);

    rows.push({ time: anchorTimeSec as Time, value: netImpact * 100 });
    clusterStart = clusterEnd;
  }

  return rows;
}

function renderPriceLegSummary(
  config: ChartRangeMeasureFundingConfig,
  t0: Time,
  t1: Time,
): string {
  const getExchange1Candles = config.getExchange1Candles;
  const getExchange2Candles = config.getExchange2Candles;
  if (!getExchange1Candles || !getExchange2Candles) return '';

  const startTime = timeToUnix(t0) <= timeToUnix(t1) ? t0 : t1;
  const endTime = timeToUnix(t0) <= timeToUnix(t1) ? t1 : t0;
  const direction = config.getDirection();
  const exchange1Label = config.getExchange1Label();
  const exchange2Label = config.getExchange2Label();

  const startCandle1 = findCandleAtTime(getExchange1Candles(), startTime);
  const endCandle1 = findCandleAtTime(getExchange1Candles(), endTime);
  const startCandle2 = findCandleAtTime(getExchange2Candles(), startTime);
  const endCandle2 = findCandleAtTime(getExchange2Candles(), endTime);
  if (!startCandle1 || !endCandle1 || !startCandle2 || !endCandle2) return '';

  const exchange1ChangePct = computePriceChangePct(startCandle1.open, endCandle1.close);
  const exchange2ChangePct = computePriceChangePct(startCandle2.open, endCandle2.close);
  const exchange1Impact = legPriceImpactPct(exchange1ChangePct, exchangeLegRole(direction, 1));
  const exchange2Impact = legPriceImpactPct(exchange2ChangePct, exchangeLegRole(direction, 2));

  return (
    `<div class="chart-range-measure-divider"></div>` +
    `<div class="chart-range-measure-section-title">价格涨跌</div>` +
    `<div class="chart-range-measure-row chart-range-measure-row-sub">` +
      `<span class="chart-range-measure-series" style="color:#16a34a">${exchange1Label} (${exchangeLegRole(direction, 1)})</span>` +
      `<span class="chart-range-measure-sub">` +
      `<span style="color:${fundingImpactColor(exchange1Impact)}">${formatPct(exchange1Impact)}</span>` +
      ` · ${formatPrice(startCandle1.open)} → ${formatPrice(endCandle1.close)}` +
      `</span>` +
    `</div>` +
    `<div class="chart-range-measure-row chart-range-measure-row-sub">` +
      `<span class="chart-range-measure-series" style="color:#0ea5e9">${exchange2Label} (${exchangeLegRole(direction, 2)})</span>` +
      `<span class="chart-range-measure-sub">` +
      `<span style="color:${fundingImpactColor(exchange2Impact)}">${formatPct(exchange2Impact)}</span>` +
      ` · ${formatPrice(startCandle2.open)} → ${formatPrice(endCandle2.close)}` +
      `</span>` +
    `</div>`
  );
}

interface SpreadFundingRangeMetrics {
  returnRate: number;
  exchange1ReturnRate: number;
  exchange2ReturnRate: number;
  direction: ChartRangeMeasureDirection;
  exchange1Label: string;
  exchange2Label: string;
  exchange1Settlements: number;
  exchange2Settlements: number;
  totalSettlements: number;
}

interface KlineFundingRangeMetrics {
  exchange1LongReturnRate: number;
  exchange1ShortReturnRate: number;
  exchange2LongReturnRate: number;
  exchange2ShortReturnRate: number;
  exchange1Label: string;
  exchange2Label: string;
  exchange1Settlements: number;
  exchange2Settlements: number;
  totalSettlements: number;
}

function computeSpreadFundingRangeMetrics(
  funding: ChartRangeMeasureFundingConfig,
  t0: Time,
  t1: Time,
): SpreadFundingRangeMetrics {
  const direction = funding.getDirection();
  const exchange1Label = funding.getExchange1Label();
  const exchange2Label = funding.getExchange2Label();
  const exchange1Settlements = pointsInTimeRange(funding.getExchange1Settlements(), t0, t1);
  const exchange2Settlements = pointsInTimeRange(funding.getExchange2Settlements(), t0, t1);
  const exchange1Impact = exchange1Settlements.reduce(
    (sum, point) => sum + settlementLegImpact(direction, 1, point.value),
    0,
  );
  const exchange2Impact = exchange2Settlements.reduce(
    (sum, point) => sum + settlementLegImpact(direction, 2, point.value),
    0,
  );
  const netImpact = exchange1Impact + exchange2Impact;

  return {
    returnRate: rawImpactToReturnRate(netImpact),
    exchange1ReturnRate: exchange1Impact,
    exchange2ReturnRate: exchange2Impact,
    direction,
    exchange1Label,
    exchange2Label,
    exchange1Settlements: exchange1Settlements.length,
    exchange2Settlements: exchange2Settlements.length,
    totalSettlements: exchange1Settlements.length + exchange2Settlements.length,
  };
}

function computeKlineFundingRangeMetrics(
  funding: ChartRangeMeasureFundingConfig,
  t0: Time,
  t1: Time,
): KlineFundingRangeMetrics {
  const exchange1Label = funding.getExchange1Label();
  const exchange2Label = funding.getExchange2Label();
  const exchange1Settlements = pointsInTimeRange(funding.getExchange1Settlements(), t0, t1);
  const exchange2Settlements = pointsInTimeRange(funding.getExchange2Settlements(), t0, t1);

  return {
    exchange1LongReturnRate: exchange1Settlements.reduce(
      (sum, point) => sum + settlementChartRateImpact(point.value, 'long'),
      0,
    ),
    exchange1ShortReturnRate: exchange1Settlements.reduce(
      (sum, point) => sum + settlementChartRateImpact(point.value, 'short'),
      0,
    ),
    exchange2LongReturnRate: exchange2Settlements.reduce(
      (sum, point) => sum + settlementChartRateImpact(point.value, 'long'),
      0,
    ),
    exchange2ShortReturnRate: exchange2Settlements.reduce(
      (sum, point) => sum + settlementChartRateImpact(point.value, 'short'),
      0,
    ),
    exchange1Label,
    exchange2Label,
    exchange1Settlements: exchange1Settlements.length,
    exchange2Settlements: exchange2Settlements.length,
    totalSettlements: exchange1Settlements.length + exchange2Settlements.length,
  };
}

function renderSpreadFundingRangeSummary(metrics: SpreadFundingRangeMetrics): string {
  return (
    `<div class="chart-range-measure-divider"></div>` +
    `<div class="chart-range-measure-section-title">资金费结算</div>` +
    `<div class="chart-range-measure-meta">` +
      `共 ${metrics.totalSettlements} 次 · ${metrics.exchange1Label} ${metrics.exchange1Settlements} · ${metrics.exchange2Label} ${metrics.exchange2Settlements}` +
    `</div>` +
    `<div class="chart-range-measure-row">` +
      `<span class="chart-range-measure-series">资费收益</span>` +
      `<span class="chart-range-measure-change" style="color:${fundingImpactColor(metrics.returnRate)}">${formatReturnOnCapitalRate(metrics.returnRate)}</span>` +
    `</div>` +
    `<div class="chart-range-measure-meta chart-range-measure-meta-sub">汇总相对 200 总本金（双边均分）</div>` +
    `<div class="chart-range-measure-row chart-range-measure-row-sub">` +
      `<span class="chart-range-measure-series" style="color:#16a34a">${metrics.exchange1Label} (${exchangeLegRole(metrics.direction, 1)})</span>` +
      `<span class="chart-range-measure-sub">${metrics.exchange1Settlements} 次 · ` +
      `<span style="color:${fundingImpactColor(metrics.exchange1ReturnRate)}">${formatReturnOnCapitalRate(metrics.exchange1ReturnRate)}</span></span>` +
    `</div>` +
    `<div class="chart-range-measure-row chart-range-measure-row-sub">` +
      `<span class="chart-range-measure-series" style="color:#0ea5e9">${metrics.exchange2Label} (${exchangeLegRole(metrics.direction, 2)})</span>` +
      `<span class="chart-range-measure-sub">${metrics.exchange2Settlements} 次 · ` +
      `<span style="color:${fundingImpactColor(metrics.exchange2ReturnRate)}">${formatReturnOnCapitalRate(metrics.exchange2ReturnRate)}</span></span>` +
    `</div>`
  );
}

function renderKlineFundingRangeSummary(metrics: KlineFundingRangeMetrics): string {
  const emptyRow =
    metrics.totalSettlements === 0
      ? `<div class="chart-range-measure-meta">所选时间范围内无资费结算点</div>`
      : '';

  return (
    `<div class="chart-range-measure-divider"></div>` +
    `<div class="chart-range-measure-section-title">资金费结算</div>` +
    `<div class="chart-range-measure-meta">` +
      `共 ${metrics.totalSettlements} 次 · ${metrics.exchange1Label} ${metrics.exchange1Settlements} · ${metrics.exchange2Label} ${metrics.exchange2Settlements}` +
    `</div>` +
    emptyRow +
    `<div class="chart-range-measure-meta chart-range-measure-meta-sub">各交易所独立累计费率（做多付 / 做空收，相对单腿本金）</div>` +
    `<div class="chart-range-measure-row chart-range-measure-row-sub">` +
      `<span class="chart-range-measure-series" style="color:#16a34a">${metrics.exchange1Label}</span>` +
      `<span class="chart-range-measure-sub">${metrics.exchange1Settlements} 次</span>` +
    `</div>` +
    `<div class="chart-range-measure-row chart-range-measure-row-sub">` +
      `<span class="chart-range-measure-series">做多</span>` +
      `<span class="chart-range-measure-sub" style="color:${fundingImpactColor(metrics.exchange1LongReturnRate)}">${formatReturnOnCapitalRate(metrics.exchange1LongReturnRate)}</span>` +
    `</div>` +
    `<div class="chart-range-measure-row chart-range-measure-row-sub">` +
      `<span class="chart-range-measure-series">做空</span>` +
      `<span class="chart-range-measure-sub" style="color:${fundingImpactColor(metrics.exchange1ShortReturnRate)}">${formatReturnOnCapitalRate(metrics.exchange1ShortReturnRate)}</span>` +
    `</div>` +
    `<div class="chart-range-measure-row chart-range-measure-row-sub">` +
      `<span class="chart-range-measure-series" style="color:#0ea5e9">${metrics.exchange2Label}</span>` +
      `<span class="chart-range-measure-sub">${metrics.exchange2Settlements} 次</span>` +
    `</div>` +
    `<div class="chart-range-measure-row chart-range-measure-row-sub">` +
      `<span class="chart-range-measure-series">做多</span>` +
      `<span class="chart-range-measure-sub" style="color:${fundingImpactColor(metrics.exchange2LongReturnRate)}">${formatReturnOnCapitalRate(metrics.exchange2LongReturnRate)}</span>` +
    `</div>` +
    `<div class="chart-range-measure-row chart-range-measure-row-sub">` +
      `<span class="chart-range-measure-series">做空</span>` +
      `<span class="chart-range-measure-sub" style="color:${fundingImpactColor(metrics.exchange2ShortReturnRate)}">${formatReturnOnCapitalRate(metrics.exchange2ShortReturnRate)}</span>` +
    `</div>`
  );
}

function renderOverallReturnSummary(
  spreadReturnRate: number | null,
  fundingReturnRate: number | null,
  durationSec: number,
): string {
  if (spreadReturnRate == null && fundingReturnRate == null) return '';

  const totalReturnRate = (spreadReturnRate ?? 0) + (fundingReturnRate ?? 0);
  const aprRate = computeAnnualizedApr(totalReturnRate, durationSec);
  const parts: string[] = [];
  if (spreadReturnRate != null) {
    parts.push(`价差 ${formatReturnOnCapitalRate(spreadReturnRate)}`);
  }
  if (fundingReturnRate != null) {
    parts.push(`资费 ${formatReturnOnCapitalRate(fundingReturnRate)}`);
  }

  const aprRow =
    aprRate == null
      ? ''
      : `<div class="chart-range-measure-row chart-range-measure-row-sub">` +
        `<span class="chart-range-measure-series">年化 APR</span>` +
        `<span class="chart-range-measure-change" style="color:${fundingImpactColor(aprRate)}">${formatAprRate(aprRate)}</span>` +
        `</div>`;

  return (
    `<div class="chart-range-measure-divider"></div>` +
    `<div class="chart-range-measure-row chart-range-measure-row-total">` +
      `<span class="chart-range-measure-series">整体收益率</span>` +
      `<span class="chart-range-measure-change" style="color:${fundingImpactColor(totalReturnRate)}">${formatReturnOnCapitalRate(totalReturnRate)}</span>` +
    `</div>` +
    aprRow +
    `<div class="chart-range-measure-meta chart-range-measure-meta-sub">${parts.join(' · ')} · ${formatDuration(durationSec)} · 相对 200 本金、双边均分 · 年化按单利折算</div>`
  );
}

function renderMeasureRow(item: ChartRangeMeasureSeries, stats: RangeStats): string {
  if (item.kind === 'line') {
    const delta = stats.endPrice - stats.startPrice;
    const changeColor = delta >= 0 ? '#16a34a' : '#dc2626';
    return (
      `<div class="chart-range-measure-row">` +
        `<span class="chart-range-measure-series" style="color:${item.color}">${item.label}</span>` +
        `<span class="chart-range-measure-change" style="color:${changeColor}">${formatSeriesDelta(item, delta)}</span>` +
      `</div>` +
      `<div class="chart-range-measure-meta">` +
        `${stats.bars} 点 · ${formatDuration(stats.durationSec)} · ` +
        `${formatSeriesValue(item, stats.startPrice)} → ${formatSeriesValue(item, stats.endPrice)}` +
      `</div>`
    );
  }

  const changeColor = stats.changePct >= 0 ? '#16a34a' : '#dc2626';
  return (
    `<div class="chart-range-measure-row">` +
      `<span class="chart-range-measure-series" style="color:${item.color}">${item.label}</span>` +
      `<span class="chart-range-measure-change" style="color:${changeColor}">${formatPct(stats.changePct)}</span>` +
    `</div>` +
    `<div class="chart-range-measure-meta">` +
      `${stats.bars} 根 · ${formatDuration(stats.durationSec)} · ` +
      `${formatSeriesValue(item, stats.startPrice)} → ${formatSeriesValue(item, stats.endPrice)} · ` +
      `振幅 ${formatPct(stats.amplitudePct)}` +
    `</div>`
  );
}

function resolveMeasureMode(pane: ChartRangeMeasurePane): 'spread' | 'kline' {
  return pane.measureMode ?? (pane.spread ? 'spread' : 'kline');
}

function rangeMeasureHintText(mode: 'spread' | 'kline'): string {
  return mode === 'kline' ? '拖拽框选时间区间' : '拖拽框选涨跌幅';
}

/** 子图分界附近不触发框选，留给 lightweight-charts 原生 pane 拖动。 */
function isNearPaneSeparator(
  chart: IChartApi,
  container: HTMLElement,
  x: number,
  y: number,
  hitPx = 7,
): boolean {
  const containerRect = container.getBoundingClientRect();
  const panes = chart.panes();
  for (let index = 0; index < panes.length - 1; index += 1) {
    const paneEl = panes[index]?.getHTMLElement();
    if (!paneEl) continue;
    const paneRect = paneEl.getBoundingClientRect();
    const bottom = paneRect.bottom - containerRect.top;
    const left = paneRect.left - containerRect.left;
    const right = paneRect.right - containerRect.left;
    if (y >= bottom - hitPx && y <= bottom + hitPx && x >= left && x <= right) {
      return true;
    }
  }
  return false;
}

export function attachChartRangeMeasure(
  container: HTMLElement,
  chart: IChartApi,
  options: AttachChartRangeMeasureOptions,
): () => void {
  const { panes } = options;

  const layer = document.createElement('div');
  layer.className = 'chart-range-measure-layer';
  container.appendChild(layer);

  const box = document.createElement('div');
  box.className = 'chart-range-measure-box';
  box.hidden = true;
  layer.appendChild(box);

  const label = document.createElement('div');
  label.className = 'chart-range-measure-label';
  label.hidden = true;
  container.appendChild(label);

  const hints = panes.map((pane) => {
    const hint = document.createElement('div');
    hint.className = 'chart-range-measure-hint';
    hint.textContent = rangeMeasureHintText(resolveMeasureMode(pane));
    layer.appendChild(hint);
    return hint;
  });

  let dragging = false;
  let selectionActive = false;
  let suppressNextClick = false;
  let activePane: ChartRangeMeasurePane | null = null;
  let startX = 0;
  let startY = 0;
  let scrollLocked = false;

  const getPaneBounds = (
    paneIndex: number,
  ): { top: number; bottom: number; left: number; right: number } | null => {
    const pane = chart.panes()[paneIndex];
    if (!pane) return null;
    const paneEl = pane.getHTMLElement();
    if (!paneEl) return null;
    const containerRect = container.getBoundingClientRect();
    const paneRect = paneEl.getBoundingClientRect();
    return {
      top: paneRect.top - containerRect.top,
      bottom: paneRect.bottom - containerRect.top,
      left: paneRect.left - containerRect.left,
      right: paneRect.right - containerRect.left,
    };
  };

  const positionHints = () => {
    panes.forEach((pane, index) => {
      const bounds = getPaneBounds(pane.paneIndex);
      const hint = hints[index];
      if (!bounds) {
        hint.hidden = true;
        return;
      }
      hint.hidden = false;
      hint.style.top = `${bounds.bottom - 28}px`;
      hint.style.left = `${bounds.left + 8}px`;
    });
  };

  const clampToPane = (
    x: number,
    y: number,
    bounds: { top: number; bottom: number; left: number; right: number },
  ) => ({
    x: Math.max(bounds.left, Math.min(bounds.right, x)),
    y: Math.max(bounds.top, Math.min(bounds.bottom, y)),
  });

  const getLocalCoords = (event: MouseEvent) => {
    const rect = container.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const isInPane = (
    x: number,
    y: number,
    bounds: { top: number; bottom: number; left: number; right: number },
  ) => x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;

  const findPaneAt = (x: number, y: number): ChartRangeMeasurePane | null => {
    for (const pane of panes) {
      const bounds = getPaneBounds(pane.paneIndex);
      if (bounds && isInPane(x, y, bounds)) return pane;
    }
    return null;
  };

  const clearSelection = () => {
    box.hidden = true;
    label.hidden = true;
    label.style.visibility = '';
    selectionActive = false;
    activePane = null;
  };

  const lockChartScroll = () => {
    if (scrollLocked) return;
    chart.applyOptions({
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: false,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
    });
    scrollLocked = true;
  };

  const unlockChartScroll = () => {
    if (!scrollLocked) return;
    chart.applyOptions({
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
    });
    scrollLocked = false;
  };

  const updateBox = (
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    bounds: { top: number; bottom: number; left: number; right: number },
    measureMode: 'spread' | 'kline',
  ) => {
    const left = Math.min(x0, x1);
    if (measureMode === 'kline') {
      box.style.left = `${left}px`;
      box.style.top = `${bounds.top}px`;
      box.style.width = `${Math.abs(x1 - x0)}px`;
      box.style.height = `${bounds.bottom - bounds.top}px`;
    } else {
      const top = Math.min(y0, y1);
      box.style.left = `${left}px`;
      box.style.top = `${top}px`;
      box.style.width = `${Math.abs(x1 - x0)}px`;
      box.style.height = `${Math.abs(y1 - y0)}px`;
    }
    box.hidden = false;
  };

  const positionMeasureLabel = (
    labelEl: HTMLDivElement,
    selectionLeft: number,
    selectionTop: number,
    selectionBottom: number,
    paneIndex: number,
  ) => {
    const margin = 8;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const paneBounds = getPaneBounds(paneIndex);
    const regionTop = paneBounds?.top ?? margin;
    const regionBottom = paneBounds?.bottom ?? containerHeight - margin;

    labelEl.style.transform = 'none';
    labelEl.style.visibility = 'hidden';
    labelEl.hidden = false;
    labelEl.style.left = '0px';
    labelEl.style.top = '0px';

    const labelWidth = labelEl.offsetWidth;
    const labelHeight = labelEl.offsetHeight;

    let left = selectionLeft;
    if (left + labelWidth > containerWidth - margin) {
      left = containerWidth - labelWidth - margin;
    }
    left = Math.max(margin, left);

    const spaceAbove = selectionTop - regionTop;
    const spaceBelow = regionBottom - selectionBottom;
    let top =
      spaceAbove >= labelHeight + margin || spaceAbove >= spaceBelow
        ? selectionTop - labelHeight - margin
        : selectionBottom + margin;

    const minTop = regionTop + margin;
    const maxTop = regionBottom - labelHeight - margin;
    if (maxTop >= minTop) {
      top = Math.max(minTop, Math.min(top, maxTop));
    } else {
      top = minTop;
    }

    labelEl.style.left = `${left}px`;
    labelEl.style.top = `${top}px`;
    labelEl.style.visibility = 'visible';
  };

  const finishSelection = (
    pane: ChartRangeMeasurePane,
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    bounds: { top: number; bottom: number; left: number; right: number },
  ) => {
    const measureMode = resolveMeasureMode(pane);
    const timeScale = chart.timeScale();
    const t0 = timeScale.coordinateToTime(Math.min(x0, x1));
    const t1 = timeScale.coordinateToTime(Math.max(x0, x1));
    if (t0 == null || t1 == null) {
      clearSelection();
      return;
    }

    const durationSec = Math.abs(timeToUnix(t1) - timeToUnix(t0));

    const rows: string[] = [];
    for (const item of pane.series) {
      const stats = computeSeriesStats(item, t0, t1, measureMode);
      if (!stats) continue;
      rows.push(renderMeasureRow(item, stats));
    }

    let html = '';
    let spreadReturnRate: number | null = null;
    let fundingReturnRate: number | null = null;

    if (pane.spread) {
      const spreadMetrics = computeSpreadRangeMetrics(pane.spread, t0, t1);
      if (spreadMetrics) {
        spreadReturnRate = spreadMetrics.returnRate;
        html += renderSpreadRangeSummary(spreadMetrics);
      }
    }
    html += rows.join('');
    if (pane.funding) {
      if (measureMode === 'spread') {
        html += renderPriceLegSummary(pane.funding, t0, t1);
        const fundingMetrics = computeSpreadFundingRangeMetrics(pane.funding, t0, t1);
        fundingReturnRate = fundingMetrics.returnRate;
        html += renderSpreadFundingRangeSummary(fundingMetrics);
      } else {
        html += renderKlineFundingRangeSummary(computeKlineFundingRangeMetrics(pane.funding, t0, t1));
      }
    }
    if (measureMode === 'spread') {
      html += renderOverallReturnSummary(spreadReturnRate, fundingReturnRate, durationSec);
    }

    if (!html) {
      clearSelection();
      return;
    }

    label.innerHTML = html;
    const left = Math.min(x0, x1);
    const top = measureMode === 'kline' ? bounds.top : Math.min(y0, y1);
    const bottom = measureMode === 'kline' ? bounds.bottom : Math.max(y0, y1);
    positionMeasureLabel(label, left, top, bottom, pane.paneIndex);
    updateBox(x0, y0, x1, y1, bounds, measureMode);
    selectionActive = true;
  };

  const onMouseDown = (event: MouseEvent) => {
    if (event.button !== 0) return;
    const local = getLocalCoords(event);
    if (isNearPaneSeparator(chart, container, local.x, local.y)) {
      return;
    }
    const pane = findPaneAt(local.x, local.y);
    if (!pane) return;
    const bounds = getPaneBounds(pane.paneIndex);
    if (!bounds) return;

    dragging = true;
    activePane = pane;
    lockChartScroll();
    const clamped = clampToPane(local.x, local.y, bounds);
    startX = clamped.x;
    startY = clamped.y;
    clearSelection();
    activePane = pane;
    updateBox(startX, startY, startX, startY, bounds, resolveMeasureMode(pane));
    event.preventDefault();
    event.stopPropagation();
  };

  const onMouseMove = (event: MouseEvent) => {
    if (!dragging || !activePane) return;
    const bounds = getPaneBounds(activePane.paneIndex);
    if (!bounds) return;
    const local = getLocalCoords(event);
    const clamped = clampToPane(local.x, local.y, bounds);
    updateBox(startX, startY, clamped.x, clamped.y, bounds, resolveMeasureMode(activePane));
  };

  const onMouseUp = (event: MouseEvent) => {
    if (!dragging || !activePane) return;
    const pane = activePane;
    dragging = false;
    unlockChartScroll();
    const bounds = getPaneBounds(pane.paneIndex);
    if (!bounds) {
      clearSelection();
      return;
    }
    const local = getLocalCoords(event);
    const clamped = clampToPane(local.x, local.y, bounds);
    const measureMode = resolveMeasureMode(pane);
    const tooSmall =
      measureMode === 'kline'
        ? Math.abs(clamped.x - startX) < 4
        : Math.abs(clamped.x - startX) < 4 && Math.abs(clamped.y - startY) < 4;
    if (tooSmall) {
      clearSelection();
      return;
    }
    finishSelection(pane, startX, startY, clamped.x, clamped.y, bounds);
    suppressNextClick = true;
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') clearSelection();
  };

  const onClick = () => {
    if (dragging) return;
    if (suppressNextClick) {
      suppressNextClick = false;
      return;
    }
    if (selectionActive) clearSelection();
  };

  container.addEventListener('mousedown', onMouseDown, true);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('keydown', onKeyDown);
  container.addEventListener('click', onClick, true);

  positionHints();
  const resizeObserver = new ResizeObserver(() => {
    positionHints();
  });
  resizeObserver.observe(container);

  return () => {
    resizeObserver.disconnect();
    unlockChartScroll();
    container.removeEventListener('mousedown', onMouseDown, true);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('keydown', onKeyDown);
    container.removeEventListener('click', onClick, true);
    layer.remove();
    label.remove();
  };
}
